import type { ApiClient } from './api/ApiClient'
import { ServiceBase } from './api/ServiceBase'
import type { RequestOptions } from './api/types'

export interface FileUploadResponse {
  success: boolean
  message: string
  file_key?: string
  file_url?: string
  file_size?: number
}

export interface FileInfoResponse {
  key: string
  size?: number
  last_modified?: string
  content_type?: string
  metadata: Record<string, unknown>
  etag?: string
}

export interface FileListResponse {
  files: FileInfoResponse[]
  total: number
}

export interface PresignedUrlResponse {
  success: boolean
  presigned_url: string
  expiration: number
}

export interface FileDeleteResponse {
  success: boolean
  message: string
}

export class StorageService extends ServiceBase {
  constructor(client: ApiClient) {
    super(client, '/common/storage')
  }

  async uploadFile(file: File, folder: string = 'uploads', options?: RequestOptions): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)

    return this.post<FileUploadResponse>('/upload', formData, {
      ...options,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...options?.headers
      }
    })
  }

  async uploadFileToS3(file: File, folder: string = 'uploads', options?: RequestOptions): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)

    return this.post<FileUploadResponse>('/upload/s3', formData, {
      ...options,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...options?.headers
      }
    })
  }

  async uploadFileToMinio(file: File, folder: string = 'uploads', options?: RequestOptions): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)

    return this.post<FileUploadResponse>('/upload/minio', formData, {
      ...options,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...options?.headers
      }
    })
  }

  async downloadFile(fileKey: string, options?: RequestOptions): Promise<Blob> {
    return this.download(`/download/${encodeURIComponent(fileKey)}`, options)
  }

  async deleteFile(fileKey: string, options?: RequestOptions): Promise<FileDeleteResponse> {
    return this.delete<FileDeleteResponse>(`/delete/${encodeURIComponent(fileKey)}`, options)
  }

  async getFileInfo(fileKey: string, options?: RequestOptions): Promise<FileInfoResponse> {
    return this.get<FileInfoResponse>(`/info/${encodeURIComponent(fileKey)}`, options)
  }

  async listFiles(prefix: string = '', maxKeys: number = 100, options?: RequestOptions): Promise<FileListResponse> {
    return this.get<FileListResponse>('/list', {
      ...options,
      params: {
        prefix,
        max_keys: maxKeys,
        ...options?.params
      }
    })
  }

  async getPresignedUrl(
    fileKey: string,
    expiration: number = 3600,
    options?: RequestOptions
  ): Promise<PresignedUrlResponse> {
    return this.get<PresignedUrlResponse>(`/presigned-url/${encodeURIComponent(fileKey)}`, {
      ...options,
      params: {
        expiration,
        ...options?.params
      }
    })
  }

  async uploadMultipleFiles(
    files: File[],
    folder: string = 'uploads',
    options?: RequestOptions
  ): Promise<FileUploadResponse[]> {
    const uploadPromises = files.map(file => this.uploadFile(file, folder, options))
    return Promise.all(uploadPromises)
  }

  async uploadFileWithProgress(
    file: File,
    folder: string = 'uploads',
    onProgress?: (progress: number) => void,
    options?: RequestOptions
  ): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)

    return this.post<FileUploadResponse>('/upload', formData, {
      ...options,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...options?.headers
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      } : undefined
    })
  }
}